import Enumerable from "linq";
import { useContext, useEffect, useState } from "react";
import { PageContextValue } from "../../../core/type";
import { Appraisal, AppraisalEntry } from "../type";
import { AppraisalPageContext } from "./AppraisalPageContext";
import { Col, DatePicker, Form, Input, Row, Space } from "antd";
import {
  CopyToClipboard,
  EmailFormItem,
  Panel,
  Section,
} from "../../../core/component";
import { translate } from "../../../core";
import { dateFormat } from "../../../core/until";
import { AppraisalFormUser } from "./AppraisalFormUser";
import { AppraisalFormAddress } from "./AppraisalFormAddress";
import { AppraisalFormLines } from "./AppraisalFormLines";
import styles from "./style.module.scss";
import { useAppraisalData } from "../hook/appraisal-data-hook";
import { ContactSelect } from "../../contact/component/ContactSelect";
import {
  Contact,
  ContactWebInfo,
  WebInfoType,
} from "../../contact/type/contact";
import { InputCard } from "../../inventory/component/InputCard";

export const AppraisalForm = () => {
  const { toEntry } = useAppraisalData();
  const { form, save, entity, onFormChange } =
    useContext<PageContextValue<Appraisal, AppraisalEntry>>(
      AppraisalPageContext
    );
  const [customerId, setCustomerId] = useState<string>("");

  useEffect(() => {
    entity && form?.setFieldsValue(toEntry(entity) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const onCustomerSelectedOrBound = (contact: Contact, isBound: boolean) => {
    setCustomerId(contact?.contactId);

    if (isBound) return;

    const address = Enumerable.from(contact.addresses || []).firstOrDefault(
      (e) => !!e
    );

    form?.setFieldsValue({
      customerFirstname: contact.firstName,
      customerLastname: contact.lastName,
      customerNumber: Enumerable.from(contact.phones || []).firstOrDefault()
        ?.number,
      customerAddress: {
        street: address?.street,
        country: address?.country,
        city: address?.city,
        state: address?.state,
        zipCode: address?.zipCode,
      },
      customerEmail: Enumerable.from(contact.webInfos || []).firstOrDefault(
        (e) => e.type === WebInfoType.EmailAddress,
        {} as ContactWebInfo
      )?.value,
    });
  };

  const onLinesChanged = () => {
    const lines = form?.getFieldValue("lines");
    form?.setFieldValue("numberOfItems", lines.length);
  };

  return (
    <Form
      onFieldsChange={onFormChange}
      form={form}
      onFinish={save}
      layout="vertical"
    >
      <InputCard contained>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item name="number" label={translate("Appraisal_no")}>
              <Input disabled={true} className="d-flex flex-column" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={
                <Space style={{ height: "2rem" }}>
                  {translate("customer_no")}
                  <CopyToClipboard
                    text={customerId}
                    tooltip={translate("copy", "contact_id")}
                  />
                </Space>
              }
              name={["customer", "id"]}
            >
              <ContactSelect
                onCustomerChangedOrBound={onCustomerSelectedOrBound}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={translate("date")}
              name="date"
              rules={[
                {
                  required: true,
                  message: translate("missing_field_message", "date"),
                },
              ]}
            >
              <DatePicker format={dateFormat} className="w-100" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="appraiserId" label={translate("appraiser")}>
              <AppraisalFormUser />
            </Form.Item>
          </Col>
        </Row>
      </InputCard>
      <InputCard contained title={translate("customer", "info")}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item
              name="customerFirstname"
              label={translate("first_name")}
              labelAlign="left"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="customerLastname"
              label={translate("last_name")}
              labelAlign="left"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <AppraisalFormAddress />

        <Row gutter={[10, 10]} className="my-3">
          <Col span={12}>
            <EmailFormItem label={translate("email")} name="customerEmail" />
          </Col>
          <Col span={6}>
            <Form.Item name="customerNumber" label={translate("phone")}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={translate("number")} name="numberOfItems">
              <Input disabled={true} />
            </Form.Item>
          </Col>
        </Row>
      </InputCard>

      <InputCard contained title={translate("items")}>
        <AppraisalFormLines onChanged={onLinesChanged} />
      </InputCard>
    </Form>
  );
};
